import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { testimonials, videos } from '@/mocks/videos';

const SITE_ORIGIN = 'http://saitemold.com';

export default function About() {
  const { t } = useTranslation();

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_ORIGIN}/about`,
        url: `${SITE_ORIGIN}/about`,
        name: `关于我们 - ${t('siteName')}`,
        description: '国产91视频的品牌介绍、口碑评价与联系方式',
        isPartOf: {
          '@id': `${SITE_ORIGIN}/#website`,
        },
        breadcrumb: {
          '@id': `${SITE_ORIGIN}/about#breadcrumb`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE_ORIGIN}/about#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: t('breadcrumb.home'),
            item: `${SITE_ORIGIN}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '关于我们',
          },
        ],
      },
      {
        '@type': 'Organization',
        name: t('siteName'),
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/logo.png`,
        description:
          '国产91视频是国内领先的原创短视频发现平台，致力于挖掘和展示最优秀的国产自制视频内容。',
        sameAs: [
          SITE_ORIGIN,
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: '客户服务',
          email: 'hello@saitemold.com',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '国产91视频是什么平台？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '国产91视频是国内领先的原创短视频发现平台，专注于汇集国内创作者自制的创意短视频、微电影和搞笑视频，帮助用户发现优质国产自制视频内容。',
            },
          },
          {
            '@type': 'Question',
            name: '如何在国产91视频上传作品？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '我们欢迎所有原创视频创作者加入。您可以通过创作者计划页面提交申请，审核通过后即可上传您的作品并与数百万用户分享。',
            },
          },
          {
            '@type': 'Question',
            name: '国产91视频的内容是否免费观看？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '是的，国产91视频上的所有内容均可免费观看。我们致力于让优质国产原创视频触达每一位用户。',
            },
          },
          {
            '@type': 'Question',
            name: '如何联系国产91视频的客服团队？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '您可以通过页面底部的联系表单或发送邮件至 hello@saitemold.com 与我们取得联系。我们的客服团队会在24小时内回复您。',
            },
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      {/* Page Header */}
      <section className="pt-24 pb-12 px-4 md:px-8 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <nav aria-label="面包屑导航" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-foreground-700 transition-colors">
                  {t('breadcrumb.home')}
                </Link>
              </li>
              <li>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </li>
              <li className="text-foreground-900 font-medium">关于我们</li>
            </ol>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900">
            {t('about.title')}
          </h1>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-sm text-accent-600 font-medium uppercase tracking-wider mb-4 block">
                {t('about.brandTitle')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                让每一个好视频都被看见
              </h2>
              <div className="space-y-4 text-foreground-600 leading-relaxed">
                <p>
                  国产91视频成立于2024年，是国内首家专注于国产原创短视频的发现与推荐平台。我们相信，中国创作者拥有无穷的创造力，而缺少的只是一个让好作品被看见的窗口。
                </p>
                <p>
                  平台汇集了搞笑视频、创意微电影、生活记录、才艺展示、美食探店、旅行风景等六大核心分类，覆盖国内创作者最活跃的领域。从北漂青年的奋斗故事到西藏高原的壮丽风光，从深夜火锅店的烟火气到乡村咖啡馆的诗意栖居——每一帧画面都是对生活的热爱与记录。
                </p>
                <p>
                  我们采用智能推荐算法与人工精选相结合的方式，确保每一位用户都能在国产91视频发现触动内心的内容。截至目前，平台已入驻超过500位签约创作者，日活跃用户突破2000万，累计播放量超过50亿次。
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20creative%20studio%20workspace%20with%20young%20Chinese%20team%20collaborating%20around%20computers%20and%20monitors%2C%20warm%20natural%20lighting%20through%20large%20windows%2C%20plants%20and%20creative%20decor%2C%20professional%20yet%20casual%20atmosphere%2C%20editorial%20lifestyle%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="国产91视频团队工作场景"
                className="w-full rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                <i className="ri-compass-3-line" />
              </span>
              <h3 className="text-lg font-bold text-foreground-900 mb-3">
                {t('about.mission')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                挖掘和推广最优秀的国产原创视频内容，让创作者的才华被更多人看见，让观众随时随地发现触动心灵的好视频。
              </p>
            </div>
            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className="ri-eye-line" />
              </span>
              <h3 className="text-lg font-bold text-foreground-900 mb-3">
                {t('about.vision')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                成为中国最具影响力的原创视频发现平台，连接创作者与观众，构建健康、多元、富有创造力的中文视频内容生态。
              </p>
            </div>
            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                <i className="ri-heart-3-line" />
              </span>
              <h3 className="text-lg font-bold text-foreground-900 mb-3">
                {t('about.values')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                尊重原创、鼓励多元、追求品质。我们坚信好的内容自带光芒，平台的价值在于让这道光芒照亮更多人的日常。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 md:py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-foreground-900 mb-1">
                500+
              </p>
              <p className="text-sm text-foreground-500">签约创作者</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-foreground-900 mb-1">
                2000万+
              </p>
              <p className="text-sm text-foreground-500">日活跃用户</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-foreground-900 mb-1">
                50亿+
              </p>
              <p className="text-sm text-foreground-500">累计播放量</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-foreground-900 mb-1">
                6
              </p>
              <p className="text-sm text-foreground-500">核心分类</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials / Reputation */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm text-accent-600 font-medium uppercase tracking-wider mb-3 block">
              {t('about.reputationTitle')}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              来自用户与创作者的声音
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tItem) => (
              <div
                key={tItem.id}
                className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70"
              >
                <div className="w-8 h-8 flex items-center justify-center text-accent-400 mb-4">
                  <i className="ri-double-quotes-l text-2xl" />
                </div>
                <p className="text-foreground-600 leading-relaxed mb-6 text-sm md:text-base">
                  {tItem.content}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-0.5 bg-accent-500" />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">
                      {tItem.author}
                    </p>
                    <p className="text-xs text-foreground-500">{tItem.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm text-primary-600 font-medium uppercase tracking-wider mb-3 block">
              {t('about.faq')}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              常见问题解答
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: '国产91视频是什么平台？',
                a: '国产91视频是国内领先的原创短视频发现平台，专注于汇集国内创作者自制的创意短视频、微电影和搞笑视频，帮助用户发现优质国产自制视频内容。',
              },
              {
                q: '如何在国产91视频上传作品？',
                a: '我们欢迎所有原创视频创作者加入。您可以通过创作者计划页面提交申请，审核通过后即可上传您的作品并与数百万用户分享。我们特别欢迎搞笑视频、创意微电影、生活记录、才艺展示、美食探店和旅行风景等内容。',
              },
              {
                q: '国产91视频的内容是否免费观看？',
                a: '是的，国产91视频上的所有内容均可免费观看。我们致力于让优质国产原创视频触达每一位用户，不设会员门槛，不插贴片广告，为您营造纯粹的观影体验。',
              },
              {
                q: '如何联系国产91视频的客服团队？',
                a: '您可以通过页面底部的联系表单或发送邮件至 hello@saitemold.com 与我们取得联系。我们的客服团队会在24小时内回复您。如有紧急问题，也可以通过微信公众号留言。',
              },
            ].map((faq, idx) => (
              <details
                key={idx}
                className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden group"
              >
                <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                  <h3 className="text-base font-medium text-foreground-900 pr-4">
                    {faq.q}
                  </h3>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-500 group-open:rotate-180 transition-transform">
                    <i className="ri-arrow-down-s-line" />
                  </span>
                </summary>
                <div className="px-5 md:px-6 pb-5 md:pb-6 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 px-4 md:px-8 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <span className="text-sm text-primary-600 font-medium uppercase tracking-wider mb-4 block">
                {t('about.contactTitle')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                与我们取得联系
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-8">
                无论是创作者合作、商务洽谈、用户反馈还是其他问题，我们都期待听到您的声音。请在右侧填写表单，或直接通过以下方式联系我们。
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-mail-line" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">
                      hello@saitemold.com
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-map-pin-line" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">办公地址</p>
                    <p className="text-sm font-medium text-foreground-900">
                      北京市海淀区中关村创业大街88号
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-customer-service-line" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">客服时间</p>
                    <p className="text-sm font-medium text-foreground-900">
                      周一至周日 9:00 - 21:00
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
              <form
                onSubmit={(event) => event.preventDefault()}
                className="space-y-5"
                id="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      您的姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      placeholder="请输入姓名"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      placeholder="请输入邮箱"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="contact-subject"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    咨询主题
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 bg-background-50"
                  >
                    <option value="">请选择主题</option>
                    <option value="creator">创作者合作</option>
                    <option value="business">商务合作</option>
                    <option value="feedback">用户反馈</option>
                    <option value="other">其他问题</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="请输入您的留言（最多500字）"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
                  />
                  <p className="text-xs text-foreground-400 mt-1">
                    最多500个字符
                  </p>
                </div>
                {/* Honeypot field */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />
                <button
                  type="submit"
                  className="w-full sm:w-auto bg-primary-500 text-background-50 px-8 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  提交留言
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
